// VERSION: 2026-01-25-21:35 - Quantity-based logic
/**
 * Automatic Free Gift Feature
 * Adds/removes free gift based on total cart quantity
 * Rule: Gift appears when total cart quantity >= 3
 */

class FreeGiftAutoAdd {
    constructor(config) {
        this.config = {
            variantId: config.variantId,
            productTitle: config.productTitle || 'Free Gift',
            ...config
        };

        this.processing = false;
        this.init();
    }

    init() {
        // Listen to cart updates
        document.addEventListener('DOMContentLoaded', () => {
            this.checkAndUpdateGift();
        });

        // Subscribe to cart update events
        if (typeof subscribe !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
                this.checkAndUpdateGift();
            });
        }
    }

    async checkAndUpdateGift() {
        if (this.processing) {
            return;
        }

        try {
            this.processing = true;

            // Fetch cart from API
            const cart = await this.getCart();

            // COUNT total quantity of all items excluding the gift itself
            const totalQuantityWithoutGift = cart.items
                .filter(item => item.variant_id != this.config.variantId)
                .reduce((sum, item) => sum + item.quantity, 0);

            const hasGift = this.cartHasGift(cart);
            const shouldHaveGift = totalQuantityWithoutGift >= 3; // Gift when total quantity >= 3

            // INSTANT REMOVAL: If no items left, remove gift from UI immediately
            const freeGiftItemsInUI = document.querySelectorAll('.free-gift-item');
            const hasGiftInUI = freeGiftItemsInUI.length > 0;

            if (!shouldHaveGift && hasGiftInUI) {
                this.removeGiftFromUI();
            }

            // Handle the actual API updates
            if (shouldHaveGift && !hasGift) {
                // OPTIMISTIC ADD: Show gift in UI first, then sync with backend
                await this.addGiftOptimistic();
            } else if (!shouldHaveGift && hasGift) {
                // UI already cleared above, just sync with API
                await this.removeGiftAPI(cart);
            }
        } catch (error) {
            console.error('[FreeGift] Error:', error);
        } finally {
            this.processing = false;
        }
    }

    async getCart() {
        const response = await fetch('/cart.js');
        return response.json();
    }

    cartHasGift(cart) {
        return cart.items.some(item => item.variant_id == this.config.variantId);
    }

    async addGiftOptimistic() {
        try {
            // First, trigger immediate cart refresh to show the gift
            // This will add the gift to the UI instantly
            const addPromise = fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: this.config.variantId,
                    quantity: 1,
                    properties: {
                        '_free_gift': 'true'
                    }
                })
            });

            // Immediately refresh cart UI (optimistic update)
            // This happens even before the API responds
            this.refreshCart();

            // Wait for API response in background
            const response = await addPromise;

            if (!response.ok) {
                // If API failed, refresh cart again to remove the optimistically added gift
                const error = await response.json();
                console.error('[FreeGift] Failed to add gift:', error);
                setTimeout(() => this.refreshCart(), 500);
            }
        } catch (error) {
            console.error('[FreeGift] Error adding gift:', error);
            // Refresh to sync with actual cart state
            setTimeout(() => this.refreshCart(), 500);
        }
    }

    async removeGiftAPI(cart) {

        // Find gift item line number
        const giftItem = cart.items.find(item => item.variant_id == this.config.variantId);
        if (!giftItem) return;

        const lineNumber = cart.items.indexOf(giftItem) + 1;

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    line: lineNumber,
                    quantity: 0
                })
            });

            if (response.ok) {
                // Refresh cart in background
                this.refreshCart();
            } else {
                const error = await response.json();
                // Revert UI if failed
                await this.refreshCart();
            }
        } catch (error) {
            // Revert UI if failed
            await this.refreshCart();
        }
    }

    removeGiftFromUI() {
        // INSTANT REMOVAL - Hide only free gift items by class
        const freeGiftItems = document.querySelectorAll('.free-gift-item');

        if (freeGiftItems.length === 0) {
            return;
        }

        freeGiftItems.forEach(item => {
            // Completely hide the item instantly
            item.style.display = 'none';
            item.style.height = '0';
            item.style.overflow = 'hidden';
            item.style.margin = '0';
            item.style.padding = '0';
        });

        // Update cart count in header immediately
        this.updateCartCount(-1);

        // Check if cart only has the gift item (meaning cart should be empty)
        const allCartItems = document.querySelectorAll('.cart-item');
        const visibleNonGiftItems = Array.from(allCartItems).filter(item => {
            return !item.classList.contains('free-gift-item') &&
                item.style.display !== 'none';
        });

        // If no other items visible, show empty cart state immediately
        if (visibleNonGiftItems.length === 0) {
            const cartDrawer = document.querySelector('cart-drawer');
            const drawerInner = document.querySelector('.drawer__inner');

            if (cartDrawer) {
                cartDrawer.classList.add('is-empty');
            }
            if (drawerInner) {
                drawerInner.classList.add('is-empty');
            }

            // Hide cart items wrapper
            const cartItemsWrapper = document.querySelector('.drawer__cart-items-wrapper');
            if (cartItemsWrapper) {
                cartItemsWrapper.style.display = 'none';
            }
        }
    }

    updateCartCount(delta) {
        const cartBubbles = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
        cartBubbles.forEach(bubble => {
            const currentCount = parseInt(bubble.textContent) || 0;
            const newCount = Math.max(0, currentCount + delta);
            bubble.textContent = newCount;
            if (newCount === 0) {
                bubble.classList.add('hidden');
            }
        });
    }

    async refreshCart() {
        // Refresh cart sections immediately (no delay)
        try {
            const response = await fetch('/cart?sections=cart-drawer,cart-icon-bubble,main-cart-footer');
            const sections = await response.json();

            // Update cart drawer if exists
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && sections['cart-drawer']) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
                const newContent = doc.querySelector('#CartDrawer');
                const currentContent = cartDrawer.querySelector('#CartDrawer');
                if (newContent && currentContent) {
                    currentContent.innerHTML = newContent.innerHTML;
                }
            }

            // Update cart icon bubble
            if (sections['cart-icon-bubble']) {
                const iconBubble = document.getElementById('cart-icon-bubble');
                if (iconBubble) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
                    const newBubble = doc.querySelector('.shopify-section');
                    if (newBubble) {
                        iconBubble.innerHTML = newBubble.innerHTML;
                    }
                }
            }

            // Trigger cart update event AFTER refresh completes
            if (typeof publish !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
            }
        } catch (e) {
            console.error('[FreeGift] Error refreshing cart:', e);
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.freeGiftAutoAdd = new FreeGiftAutoAdd({
        variantId: '48300251578623',  // Free gift variant ID
        productTitle: 'Free Gift'      // Gift product name
    });
});
